import { AsyncPipe, DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { SnapshotImageContract } from '@polaris-customerweb/sharedcomponents.core';
import {
    DateTimeService,
    DistanceDisplayComponent,
    DurationDisplayComponent,
    ImageCdnSrcDirective,
    TimeDisplayService,
    ToDistancePipe,
    ToDistanceUnitsPipe,
} from '@polaris-customerweb/sharedcomponents.shared';
import { UserProfileService } from '@polaris-customerweb/sharedcomponents.user-profile';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { firstValueFrom } from 'rxjs';
import { LikeButtonComponent } from 'src/app/components/shared/components/like-button/like-button.component';
import { RatingDisplayComponent } from 'src/app/components/shared/components/rating/rating-display/rating-display.component';
import { AnalyticsDataDirective } from 'src/app/directives/analytics-data.directive';
import { AnalyticsDirective } from 'src/app/directives/analytics.directive';
import { CommunityItem } from 'src/app/models/community-item';
import { Tag } from 'src/app/models/tag';
import { CommunityItemService } from 'src/app/services/community-item.service';
import { SnapshotService } from 'src/app/services/snapshot.service';
import { UserContentService } from 'src/app/services/user-content.service';
import { WaypointService } from 'src/app/services/waypoint.service';

@Component({
    imports: [
        AnalyticsDataDirective,
        AnalyticsDirective,
        AngularSvgIconModule,
        AsyncPipe,
        DecimalPipe,
        DistanceDisplayComponent,
        DurationDisplayComponent,
        ImageCdnSrcDirective,
        LikeButtonComponent,
        RatingDisplayComponent,
        RouterLink,
        ToDistancePipe,
        ToDistanceUnitsPipe,
    ],
    selector: 'rdx-activity-card',
    styleUrls: ['./activity-card.component.scss'],
    templateUrl: './activity-card.component.html',
})
export class ActivityCardComponent implements OnInit {
    @Input() communityItem: CommunityItem;
    @Input() rsvpStatus: RSVPStatus;
    @Input() showRideEventVisibility = false;
    @Input() showViewOnMap = true;
    @Input() context = 'None';
    @Input() subContext: number;
    @Input() waypointTags: Tag[];

    @Output() detailsClick = new EventEmitter<CommunityItem>();

    comments: number;
    distance: number;
    duration: number;
    eventDate: string;
    isOnRoad: boolean;
    isOffRoad: boolean;
    itemCount: number;
    numberOfDislikes: number;
    ratings: number;
    sharedByDate: string;
    sharedByName: string;
    snapshots: readonly SnapshotImageContract[];
    speed?: number;
    thumbnailUrl: SafeResourceUrl | null = null;
    rideTypeIcon: string;
    viewOnMapUrl: string;
    isOwned = false;
    waypointTag?: Tag;
    isEmptyCollection = false;

    constructor(
        private configService: ConfigService,
        private communityItemService: CommunityItemService,
        private datetimeService: DateTimeService,
        private router: Router,
        private sanitizer: DomSanitizer,
        private snapshotService: SnapshotService,
        private timeDisplayService: TimeDisplayService,
        private userProfileService: UserProfileService,
        private waypointService: WaypointService
    ) {
        this.isOnRoad = this.configService.isOnRoad;
        this.isOffRoad = this.configService.isOffRoad;
    }

    private static getRideTypeIcon(rideType: string) {
        if (rideType === 'ORV') {
            return '/assets/images/list-views/feed-icon-sport-orv.png';
        } else if (rideType === 'SNO') {
            return '/assets/images/list-views/feed-icon-sport-snow.png';
        } else {
            return null;
        }
    }

    ngOnInit(): void {
        this.calculateDisplayItems();
        if (this.communityItem.content?.event?.coverImage?.thumbnailUrl) {
            this.thumbnailUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
                this.communityItem.content.event.coverImage.thumbnailUrl
            );
        } else {
            const snapshotUrl = this.snapshotService.getSnapshotUrl(
                this.snapshots,
                ['230x230']
            );
            const addedImages = this.communityItem.content?.contentItem?.images;

            this.isEmptyCollection =
                this.communityItem.linkedType === 'Collection' && !snapshotUrl;

            if (this.snapshots?.length) {
                this.thumbnailUrl =
                    this.sanitizer.bypassSecurityTrustResourceUrl(snapshotUrl);
            } else if (addedImages?.length) {
                this.thumbnailUrl =
                    this.sanitizer.bypassSecurityTrustResourceUrl(
                        addedImages?.[0]?.thumbnailUrl ?? ''
                    );
            }
        }

        if (
            this.communityItem?.linkedType === 'Waypoint' &&
            this.waypointTags?.length > 0
        ) {
            this.waypointTag = this.waypointService.getWaypointTag(
                this.communityItem.content.waypoint,
                this.waypointTags
            );
        }

        void firstValueFrom(
            this.userProfileService.isOwner$(this.communityItem.riderId)
        ).then((isOwned) => {
            this.isOwned = isOwned;
        });
    }

    onViewMapClick() {
        let itemName = this.communityItem.name;
        if (this.subContext !== undefined) {
            itemName += `|${this.subContext}`;
        }
        this.communityItemService.setMostRecentlyViewedItem(
            this.context,
            itemName
        );
        void this.router.navigate([this.viewOnMapUrl]);
    }

    onDetailsClick() {
        this.detailsClick.emit(this.communityItem);
    }

    calculateDisplayItems() {
        this.rideTypeIcon =
            ActivityCardComponent.getRideTypeIcon(
                this.communityItem.rideType
            ) || '';
        this.snapshots =
            this.communityItem.content?.contentItem?.snapshots || [];
        this.viewOnMapUrl = this.getMapUrl(this.communityItem);

        if (this.communityItem.communityFeedback) {
            this.comments =
                this.communityItem.communityFeedback.comments?.length || 0;
            const dislikes =
                this.communityItem.communityFeedback.likeCounts?.find(
                    (i) => i.type === 'Dislike'
                );
            this.numberOfDislikes = dislikes?.count || 0;
            this.ratings =
                this.communityItem.communityFeedback.ratingAverage || 0;
            if (this.communityItem.creationDate) {
                this.sharedByDate = this.timeDisplayService.getUpdateTime(
                    this.communityItem.creationDate
                );
            }
            this.sharedByName = this.communityItem.sharedByName;
        }

        if (this.communityItem.linkedType === 'PlannedRide') {
            this.distance = this.communityItem.content.totalDistanceInMeters;
            this.duration = this.communityItem.content.totalDurationInSeconds;
        }

        if (this.communityItem.linkedType === 'TrackedRide') {
            this.distance = this.communityItem.content.totalDistanceInMeters;
            this.duration = this.communityItem.content.totalDurationInSeconds;
            this.speed = this.communityItem.content.trackedRide.averageSpeed;
        }

        if (this.communityItem.linkedType === 'Collection') {
            this.itemCount = this.communityItem.content.collection.itemCount;
        }

        if (this.communityItem.linkedType === 'Event') {
            this.eventDate = this.datetimeService.getRelativeWeekdayOrMonth(
                this.communityItem.content.event.startDate
            );
        }
    }

    getMapUrl(item: CommunityItem) {
        return UserContentService.GetViewOnMapUrl(
            item.content?.contentItem?.id || '',
            item.linkedType,
            '',
            item.id,
            '',
            ''
        );
    }
}
